<template>
  <div id="app">
    <AppBackdrop />

    <AppNavbar />

    <div class="page">
      <RouterView />
    </div>

    <AppFooter />

    <div class="site-popups">
      <CookieNotice />
      <SignUpForm />
    </div>
  </div>
</template>

<script setup lang="ts">
import 'bootstrap/scss/bootstrap.scss';
import { watch, onMounted, onBeforeUnmount } from 'vue';
import { storeToRefs } from 'pinia';
import { useAppStore } from '@/stores/app';

import AppBackdrop from '@/components/AppBackdrop.vue';
import AppNavbar from '@/components/AppNavbar.vue';
import AppFooter from '@/components/AppFooter.vue';
import CookieNotice from '@/components/CookieNotice.vue';
import SignUpForm from '@/components/SignUpForm.vue';

const store = useAppStore();
const { mobile, theme, scrollDisabled } = storeToRefs(store);

function rootAttr(name: string, value: string) {
  document.documentElement.setAttribute(`data-${name}`, value);
}

// Watch for changes
watch(
  mobile,
  (isMobile) => {
    rootAttr('display-type', isMobile ? 'mobile' : 'desktop');
  },
  { immediate: true },
);

watch(
  theme,
  (currentTheme) => {
    rootAttr('theme', currentTheme);
  },
  { immediate: true },
);

watch(
  scrollDisabled,
  (locked) => {
    rootAttr('scrolling-disabled', locked ? 'true' : 'false');
  },
  { immediate: true },
);

function isMobileCheck() {
  const isMobile = window.innerWidth <= 800;
  if (mobile.value !== isMobile) {
    store.setMobile(isMobile);
  }
}

onMounted(() => {
  window.addEventListener('resize', isMobileCheck);
  isMobileCheck();
});

onBeforeUnmount(() => {
  window.removeEventListener('resize', isMobileCheck);
});
</script>

<style lang="scss" scoped>
#app {
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.page {
  position: relative;
  min-height: calc(100vh - 525px);
  margin-top: calc(-1 * #{$navbar-height});
  z-index: 5;
}

.site-popups {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 6;
}

@media screen and (max-width: $mobile-screen-size) {
  .page {
    position: relative;
    padding-top: 80px;
  }
}
</style>

<style src="@/styles/general.scss" lang="scss"></style>
<style src="@/styles/base.scss" lang="scss"></style>
